/**
 * Zod validation schemas + constants for the in-service newsletter (Epic 20 S20.20).
 *
 * Authenticated, SINGLE-opt-in newsletter subscription for registered users.
 * Distinct from the public Originals beta-waitlist signup (S20.9/S20.19): a
 * dedicated Brevo list, no double opt-in, and this endpoint requires auth. Brevo
 * is the sole subscriber register — there is NO Firestore write.
 */

import { z } from 'zod';

/**
 * Canonical newsletter consent text, per language.
 *
 * SINGLE SOURCE OF TRUTH (AC11): the `submitNewsletterSubscription` Cloud Function
 * stores `NEWSLETTER_CONSENT_TEXT[language]` verbatim as the Brevo `CONSENT_TEXT`
 * attribute — the language the subscriber actually saw. NEVER pass the displayed
 * string from the client (tamperable); the server looks it up by `language`.
 */
export const NEWSLETTER_CONSENT_TEXT: Record<'fi' | 'en', string> = {
  fi: 'Tilaan Sagamoonin uutiskirjeen ja hyväksyn, että sähköpostiosoitettani käytetään uutiskirjeen, tuotepäivitysten ja käyttövinkkien lähettämiseen. Voin peruuttaa tilauksen milloin tahansa.',
  en: 'I subscribe to the Sagamoon newsletter and agree that my email address is used to send the newsletter, product updates and usage tips. I can unsubscribe at any time.',
};

/**
 * Request payload for `submitNewsletterSubscription` (S20.20).
 *
 * `consent` MUST be `true` — the "Tilaa" click IS the consent (no checkbox; the
 * page is unambiguously a subscription form). `language` selects both the stored
 * consent text and the Brevo `LANGUAGE` attribute. Server-side contract; there is
 * NO Firestore write (Brevo-only).
 */
export const SubmitNewsletterSubscriptionRequestSchema = z.object({
  email: z.string().email(),
  consent: z.literal(true),
  language: z.enum(['fi', 'en']),
});

export type SubmitNewsletterSubscriptionRequest = z.infer<
  typeof SubmitNewsletterSubscriptionRequestSchema
>;

/** Response from `submitNewsletterSubscription`. Soft success on duplicate/already-subscribed (AC4). */
export interface SubmitNewsletterSubscriptionResponse {
  ok: true;
}
